import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InviteSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, user } = useAuth();
  const token = searchParams.get('token') || '';

  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({ name: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingInvite, setCheckingInvite] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
      return;
    }

    const verifyInvite = async () => {
      if (!token) {
        setError('Invite link is missing or invalid.');
        setCheckingInvite(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/invites/verify`, { params: { token } });
        setEmail(response.data.email || '');
      } catch (err) {
        setError(err.response?.data?.detail || 'Invite link is invalid or expired.');
      } finally {
        setCheckingInvite(false);
      }
    };

    verifyInvite();
  }, [token, user, navigate]);

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signup(email, formData.password, formData.name, token);
      if (!result.success) {
        setError(result.error || 'Signup failed.');
        setLoading(false);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1.25rem'
    }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #c7ddff',
          borderRadius: '18px',
          padding: '2rem 1.75rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ fontSize: 'clamp(1.6rem, 6vw, 2.1rem)', color: '#1f2937', marginBottom: '0.45rem' }}>Accept your invite</h1>
            <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.5' }}>
              You&apos;ve been invited to start a 15‑day free trial of QuotePro.
            </p>
          </div>

          {checkingInvite ? (
            <p style={{ color: '#64748b', textAlign: 'center' }}>Checking invite...</p>
          ) : (
            <>
              {error && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  padding: '0.75rem 1rem',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  fontSize: '0.95rem'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ fontSize: '0.95rem', color: '#334155' }}>Full Name</label>
                  <div style={{ position: 'relative' }}>
                    <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      style={{ paddingLeft: '40px' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ fontSize: '0.95rem', color: '#334155' }}>Email Address</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="email"
                      className="form-input"
                      value={email}
                      disabled
                      style={{ paddingLeft: '40px', backgroundColor: '#f8fafc' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label" style={{ fontSize: '0.95rem', color: '#334155' }}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="form-input"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a secure password"
                      required
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#64748b'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      width: '100%',
                      padding: '0.95rem',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      boxShadow: '0 10px 24px rgba(74, 144, 226, 0.25)'
                    }}
                    disabled={loading || checkingInvite || Boolean(error)}
                  >
                    {loading ? 'Creating account...' : 'Start free trial'}
                  </button>
                </div>
              </form>

              <div style={{ marginTop: '1.25rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                Already have an account? <Link to="/login" style={{ color: '#4A90E2' }}>Sign in</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteSignup;
